import { useEffect, useRef, useState } from "react"

/**
 * Adressfelder mit Offline-Vorschlägen (Straße / PLZ / Stadt).
 *
 * Datenquelle ist die lokale geo.db auf dem Server (1,2 Mio deutsche Straßen,
 * dieselbe DB, die der Voice-Agent zur Adressvalidierung nutzt). Kein externer
 * Geocoder — es verlässt keine Kundenadresse den Server.
 *
 * Verhalten:
 *  - Straße tippen → Vorschläge, auf die PLZ eingegrenzt sobald eine da ist.
 *    Ohne PLZ wird bundesweit gesucht und der Ort mit angezeigt, sonst wäre
 *    "Kaiserstr." nicht unterscheidbar.
 *  - Auswahl füllt Straße + PLZ + Stadt; eine bereits getippte Hausnummer
 *    bleibt erhalten.
 *  - PLZ vollständig (5 Ziffern) → Stadt wird gefüllt, falls noch leer.
 *
 * Die Vorschläge sind Komfort, keine Pflicht: fehlt die geo.db, verhalten sich
 * die Felder wie ganz normale Eingabefelder.
 */
export type AdressWert = { street: string; zip: string; city: string }

type Vorschlag = { street: string; plz: string; city: string }

/**
 * #114 (Marlene, 23.09.2026): „Wie konnte durchrutschen, dass hier eine falsche
 * Adresse im Termin steht?" — „Pinzalberghausen" (Sipgate hatte
 * Pfinztal-Berghausen verhoert) stand ohne PLZ im Kontakt, im Projekt, im
 * Termin und in der Terminbestaetigung. Niemand hat den Ort je geprueft.
 *
 * Jetzt: sobald PLZ oder Ort dastehen, fragt das Feld die geo.db — kennt sie
 * den Ort nicht, passt er nicht zur PLZ oder gibt es die PLZ nicht, steht ein
 * Hinweis unter den Feldern, mit Vorschlaegen zum Anklicken. Eine Sperre ist
 * das bewusst nicht: Neubaugebiete und Sonder-PLZ fehlen in jeder Datenbank.
 */
export type AdressPruefung = {
  status: "ok" | "plz_unbekannt" | "ort_passt_nicht" | "ort_unbekannt" | "unbekannt"
  hinweis: string
  vorschlaege: { plz: string; city: string }[]
}

export function useAdressPruefung(street: string, zip: string, city: string): AdressPruefung | null {
  const [befund, setBefund] = useState<AdressPruefung | null>(null)
  const letzteAnfrage = useRef(0)

  useEffect(() => {
    const plz = (zip || "").trim()
    const ort = (city || "").trim()
    // Ohne PLZ und ohne Ort gibt es nichts zu pruefen; eine halbe PLZ auch nicht.
    if (!ort && !/^\d{5}$/.test(plz)) { setBefund(null); return }
    if (plz && !/^\d{5}$/.test(plz) && !ort) { setBefund(null); return }
    const id = ++letzteAnfrage.current
    const t = setTimeout(async () => {
      try {
        const p = new URLSearchParams({ street: (street || "").trim(), plz, ort })
        const r = await fetch(`/api/v1/geo/pruefen?${p}`, { credentials: "include" })
        if (!r.ok) return
        const d = await r.json() as AdressPruefung & { verfuegbar?: boolean }
        if (id !== letzteAnfrage.current) return
        setBefund(d.verfuegbar === false ? null : d)
      } catch { /* Hinweis ist optional */ }
    }, 500)
    return () => clearTimeout(t)
  }, [street, zip, city])

  return befund
}

const hausnrAus = (s: string): string => {
  const m = /\s+(\d+\s*[a-z]?(?:\s*[-/]\s*\d+\s*[a-z]?)?)\s*$/i.exec(s || "")
  return m ? m[1].trim() : ""
}

export function useAdressVorschlaege(street: string, zip: string, city: string) {
  const [items, setItems] = useState<Vorschlag[]>([])
  const [offen, setOffen] = useState(false)
  const letzteAnfrage = useRef(0)

  useEffect(() => {
    const q = (street || "").trim()
    if (q.length < 2) { setItems([]); return }
    const id = ++letzteAnfrage.current
    // Debounce: sonst eine Anfrage pro Tastendruck.
    const t = setTimeout(async () => {
      try {
        const p = new URLSearchParams({ q })
        if (/^\d{5}$/.test(zip.trim())) p.set("plz", zip.trim())
        else if (city.trim()) p.set("ort", city.trim())
        const r = await fetch(`/api/v1/geo/streets?${p}`, { credentials: "include" })
        if (!r.ok) return
        const d = await r.json()
        // Verspätete Antwort einer älteren Eingabe darf die neue nicht überschreiben.
        if (id !== letzteAnfrage.current) return
        setItems(Array.isArray(d.items) ? d.items : [])
      } catch { /* Vorschläge sind optional */ }
    }, 250)
    return () => clearTimeout(t)
  }, [street, zip, city])

  return { items, offen, setOffen }
}

export function AddressFields({
  wert, onChange, labelStrasse = "Strasse", disabled,
}: {
  wert: AdressWert
  onChange: (w: AdressWert) => void
  labelStrasse?: string
  disabled?: boolean
}) {
  const { items, offen, setOffen } = useAdressVorschlaege(wert.street, wert.zip, wert.city)
  const [aktiv, setAktiv] = useState(-1)
  const pruefung = useAdressPruefung(wert.street, wert.zip, wert.city)
  const warnung = pruefung && pruefung.status !== "ok" && pruefung.status !== "unbekannt" ? pruefung : null

  const uebernehmen = (v: Vorschlag) => {
    // Hausnummer aus der Eingabe retten — die steht nicht in der DB.
    const nr = hausnrAus(wert.street)
    onChange({
      street: nr ? `${v.street} ${nr}` : v.street,
      zip: v.plz || wert.zip,
      city: v.city || wert.city,
    })
    setOffen(false)
    setAktiv(-1)
  }

  // PLZ vollständig → Stadt nachziehen, aber nur wenn sie noch leer ist
  // (eine vom Nutzer getippte Stadt wird nicht überschrieben).
  useEffect(() => {
    const plz = (wert.zip || "").trim()
    if (!/^\d{5}$/.test(plz) || wert.city.trim()) return
    let abbruch = false
    ;(async () => {
      try {
        const r = await fetch(`/api/v1/geo/plz/${plz}`, { credentials: "include" })
        if (!r.ok) return
        const d = await r.json()
        const ort = (d.orte || [])[0]
        if (ort && !abbruch) onChange({ ...wert, city: ort })
      } catch { /* optional */ }
    })()
    return () => { abbruch = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wert.zip])

  const zeigeListe = offen && items.length > 0
  // Ohne PLZ-Kontext ist der Ort das einzige Unterscheidungsmerkmal.
  const mitOrt = !/^\d{5}$/.test((wert.zip || "").trim())

  return (
    <>
      <div style={{ marginBottom: 12, position: "relative" }}>
        <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 4 }}>{labelStrasse}</label>
        <input
          value={wert.street}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => { onChange({ ...wert, street: e.target.value }); setOffen(true); setAktiv(-1) }}
          onFocus={() => setOffen(true)}
          // Klick auf einen Vorschlag würde sonst vom Blur weggeräumt.
          onBlur={() => setTimeout(() => setOffen(false), 150)}
          onKeyDown={(e) => {
            if (!zeigeListe) return
            if (e.key === "ArrowDown") { e.preventDefault(); setAktiv((i) => Math.min(i + 1, items.length - 1)) }
            else if (e.key === "ArrowUp") { e.preventDefault(); setAktiv((i) => Math.max(i - 1, 0)) }
            else if (e.key === "Enter" && aktiv >= 0) { e.preventDefault(); uebernehmen(items[aktiv]) }
            else if (e.key === "Escape") { setOffen(false) }
          }}
          style={{ width: "100%" }}
        />
        {zeigeListe && (
          <div style={{
            position: "absolute", zIndex: 40, left: 0, right: 0, top: "100%",
            background: "var(--card-bg, #fff)", border: "1px solid var(--border)",
            borderRadius: 6, marginTop: 2, maxHeight: 220, overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,.12)",
          }}>
            {items.map((v, i) => (
              <div
                key={`${v.street}-${v.plz}-${v.city}`}
                onMouseDown={(e) => { e.preventDefault(); uebernehmen(v) }}
                onMouseEnter={() => setAktiv(i)}
                style={{
                  padding: "6px 10px", fontSize: 12.5, cursor: "pointer",
                  background: i === aktiv ? "var(--secondary-bg)" : "transparent",
                }}
              >
                {v.street}
                {mitOrt && (
                  <span style={{ color: "var(--text-muted)" }}> · {v.plz} {v.city}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 4 }}>PLZ</label>
          <input
            value={wert.zip}
            disabled={disabled}
            inputMode="numeric"
            autoComplete="off"
            onChange={(e) => onChange({ ...wert, zip: e.target.value })}
            style={{ width: "100%" }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: 12, opacity: 0.75, marginBottom: 4 }}>Stadt</label>
          <input
            value={wert.city}
            disabled={disabled}
            autoComplete="off"
            onChange={(e) => onChange({ ...wert, city: e.target.value })}
            style={{ width: "100%" }}
          />
        </div>
      </div>
      {warnung && (
        <div
          data-testid="adress-warnung"
          role="status"
          style={{
            marginTop: -6, marginBottom: 12, padding: "6px 10px", fontSize: 12.5,
            background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.5)",
            borderRadius: 6, color: "#78350f",
          }}
        >
          <span>⚠︎ {warnung.hinweis}</span>
          {warnung.vorschlaege.length > 0 && (
            <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 6 }}>
              <span style={{ opacity: 0.8 }}>Meinten Sie:</span>
              {warnung.vorschlaege.map((v) => (
                <button
                  key={`${v.plz}-${v.city}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange({ ...wert, zip: v.plz || wert.zip, city: v.city || wert.city })}
                  style={{
                    border: "1px solid rgba(120,53,15,0.35)", borderRadius: 999, padding: "1px 8px",
                    background: "#fff", cursor: "pointer", fontSize: 12, color: "#78350f",
                  }}
                >
                  {v.plz} {v.city}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
